import React from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { WarningOutlined } from '@ant-design/icons';
import { CalendarClock, Info, Pencil, Receipt, StickyNote, Trash2, Wallet } from 'lucide-react';
import GenericTooltip from '../../reuse/Tooltip/GenericToolTip';
import PaymentDetails from '../../payment/PaymentDetails';
import DisputeRespond from '../BookingModals/DisputeRespond';
import { Card } from './Card';
import ActionButton from './ActionButton';

/**
 * The sidebar's payment section: Charge & Discounts, Balance (EMIs / remaining amount), one grey
 * block per payment, and Payment Notes.
 *
 * Every amount is computed in CamperDetail exactly as the old panel did; this file only lays it out.
 * Money values arrive as numbers (or null) and are shown as `$0.00` when empty.
 */

export const money = value => `$${Number(value || 0).toFixed(2)}`;

const Row = ({ label, children, strong }) => (
    <div className={`tw-flex tw-items-baseline tw-justify-between tw-gap-3 tw-text-sm ${strong ? 'tw-font-semibold tw-text-neutral-900' : 'tw-text-neutral-700'}`}>
        <span className={strong ? '' : 'tw-text-neutral-500'}>{label}</span>
        <span className="tw-text-right tw-tabular-nums">{children}</span>
    </div>
);

/** `lines` and `totals` are [{ label, value }]; values are already display strings. */
export const ChargesCard = ({ lines, totals, finalAmount }) => (
    <Card
        id="charges"
        title="Charge & Discounts"
        icon={Receipt}
        actions={finalAmount
            ? <OverlayTrigger
                placement="left"
                delay={{ show: 250, hide: 400 }}
                overlay={<Tooltip>
                    <ul className="tw-m-0 tw-list-none tw-p-0 tw-text-left">
                        <li>Final Confirmed Amount: ${finalAmount.amount}</li>
                        <li>Reason: {finalAmount.reason}</li>
                    </ul>
                </Tooltip>}
            >
                <button type="button" aria-label="Final confirmed amount" className="tw-text-brand-600 hover:tw-text-brand-700">
                    <Info className="tw-h-4 tw-w-4" aria-hidden="true" />
                </button>
            </OverlayTrigger>
            : null}
    >
        <div className="tw-flex tw-flex-col tw-gap-1.5">
            {lines.map(l => <Row key={l.label} label={l.label}>{l.value}</Row>)}
        </div>
        <div className="tw-mt-3 tw-flex tw-flex-col tw-gap-1.5 tw-border-t tw-border-neutral-200 tw-pt-3">
            {totals.map(l => <Row key={l.label} label={l.label} strong>{l.value}</Row>)}
        </div>
    </Card>
);

/**
 * EMIs due (status 2 pending / 3 failed) with their actions, or else the remaining amount, plus the
 * next payment date and payments left. Same conditions and handlers as before.
 */
export const BalanceCard = ({
    emis, remainingAmount, nextPayment, formatDate, payMuted,
    onScheduleEmi, onEditEmiDate, onUpdateEmiAmount, onDeleteEmi, onPayEmi, onUpdateRemaining, onPayRemaining,
}) => {
    const due = (emis || []).filter(e => e.status === 2 || e.status === 3);
    return (
        <Card
            id="balance"
            title="Balance"
            icon={Wallet}
            actions={<ActionButton variant="primary" icon={CalendarClock} onClick={onScheduleEmi}>Schedule EMI</ActionButton>}
        >
            {emis?.length && !due.length
                ? <p className="tw-text-sm tw-italic tw-text-neutral-400">No installments due.</p>
                : null}
            {emis?.length
                ? <ul className="tw-flex tw-flex-col">
                    {due.map(emi => (
                        <li key={emi._id} className="tw-flex tw-flex-col tw-gap-2 tw-border-t tw-border-neutral-100 tw-py-3 first:tw-border-t-0 first:tw-pt-0 last:tw-pb-0">
                            <div className="tw-flex tw-items-center tw-justify-between tw-gap-2 tw-text-sm">
                                <span className="tw-inline-flex tw-items-center tw-gap-1.5 tw-text-neutral-600">
                                    Due: {formatDate(emi.emiDate, 'DD MMM, YYYY')}
                                    <button type="button" aria-label="Change due date" onClick={() => onEditEmiDate(emi)} className="tw-text-neutral-400 hover:tw-text-brand-600">
                                        <Pencil className="tw-h-3.5 tw-w-3.5" aria-hidden="true" />
                                    </button>
                                </span>
                                <span className="tw-font-medium tw-tabular-nums tw-text-neutral-900">{money(emi.emiAmount)}</span>
                            </div>
                            <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-2">
                                <ActionButton variant="primary" onClick={() => onUpdateEmiAmount(emi)}>Update Amount</ActionButton>
                                <div className="tw-flex tw-items-center tw-gap-2">
                                    {(emi.status === 3 || emi.isPendingCompletion)
                                        ? <GenericTooltip
                                            type="warning"
                                            tooltipText={emi.isPendingCompletion
                                                ? 'Your payment is being processed. This may take up to 3 days.'
                                                : (emi.transactions?.length > 0
                                                    ? `Reason : ${emi.transactions.at(-1)?.errorMessage ? emi.transactions.at(-1).errorMessage : 'Last payment failed'}`
                                                    : 'Reason: Last payment failed')}
                                            icon={<WarningOutlined style={{ fontSize: '20px', color: 'orange' }} />}
                                        />
                                        : null}
                                    {emi.type === 1
                                        ? <button type="button" aria-label="Delete EMI" onClick={() => onDeleteEmi(emi)} className="tw-p-1 tw-text-neutral-400 hover:tw-text-red-600">
                                            <Trash2 className="tw-h-4 tw-w-4" aria-hidden="true" />
                                        </button>
                                        : null}
                                    {/* A payment still processing can't be paid again; the old button had no handler. */}
                                    <ActionButton variant="primary" disabled={!!emi.isPendingCompletion} onClick={() => onPayEmi(emi)}>Pay Now</ActionButton>
                                </div>
                            </div>
                        </li>
                    ))}
                </ul>
                : remainingAmount > 0
                    ? <div className="tw-flex tw-flex-col tw-gap-2">
                        <Row label="Remaining Amount">{money(remainingAmount)}</Row>
                        <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-2">
                            <ActionButton variant="primary" onClick={onUpdateRemaining}>Update Remaining Amount</ActionButton>
                            <ActionButton variant="primary" className={payMuted ? 'tw-opacity-60' : ''} onClick={onPayRemaining}>Pay Now</ActionButton>
                        </div>
                    </div>
                    : <p className="tw-text-sm tw-italic tw-text-neutral-400">No remaining balance.</p>}

            {nextPayment
                ? <div className="tw-mt-3 tw-flex tw-flex-col tw-gap-1.5 tw-border-t tw-border-neutral-200 tw-pt-3">
                    <Row label="Date of Next Payment">{nextPayment.date}</Row>
                    <Row label="# Of Payments Left">{nextPayment.left}</Row>
                </div>
                : null}
        </Card>
    );
};

const Badge = ({ color, children }) => (
    <span className="tw-inline-block tw-rounded tw-px-2 tw-py-0.5 tw-text-xs tw-font-semibold tw-text-white" style={{ background: color }}>{children}</span>
);

// Old page's look (client request): black label, body-grey value (#747474).
const PaymentRow = ({ label, children }) => (
    <div className="tw-grid tw-grid-cols-[120px_minmax(0,1fr)] tw-gap-2 tw-text-sm">
        <span className="tw-font-semibold tw-text-neutral-900">{label}</span>
        <span className="tw-break-all tw-text-neutral-500">{children}</span>
    </div>
);

const DISPUTE_COLORS = { won: 'green', lost: 'red', needs_response: '#e6a917' };

const DisputeInfo = ({ dispute, formatDate, dueLabel, canRespond, onDisputeClose }) => {
    const label = dispute.status.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
    return (
        <div className="tw-flex tw-flex-col tw-gap-1.5">
            <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-2">
                <OverlayTrigger placement="top" overlay={<Tooltip>{dispute.reason}</Tooltip>}>
                    <span><Badge color={DISPUTE_COLORS[dispute.status] || '#6c757d'}>Dispute: {label}</Badge></span>
                </OverlayTrigger>
                <span className="tw-text-xs tw-text-neutral-500">{formatDate(dispute.updatedAt)}</span>
            </div>
            {dispute.status === 'needs_response' && dispute.dueDate
                ? <small className="tw-text-red-600">Respond by {dueLabel(dispute.dueDate)}</small>
                : null}
            {/* Directors and SuperDirectors only - the token endpoint refuses everyone else. */}
            {canRespond ? <div><DisputeRespond dispute={dispute} onClose={onDisputeClose} /></div> : null}
        </div>
    );
};

/**
 * One grey block per payment (the old page's #f2f2f2, client request; rounded corners from the design), with "Payment Details" (card / bank lookup) at the
 * bottom. Status badges, refunds and the latest dispute keep the old colours and rules.
 * Without `payments`, the booking's single `payment` is shown the same way (no details lookup).
 */
export const PaymentsList = ({ payments, fallback, emiDateFor, formatDate, dueLabel, canceled, refunded, canRespondDispute, onDisputeClose }) => {
    const frame = `tw-overflow-hidden tw-rounded-lg tw-bg-[#f2f2f2] ${canceled ? 'tw-ring-2 tw-ring-inset tw-ring-red-500' : ''} ${refunded ? 'tw-opacity-90' : ''}`;

    if (!payments.length) {
        return (
            <div className={`booking-detail-page ${frame}`}>
                <div className="tw-flex tw-flex-col tw-gap-2 tw-p-4">
                    <PaymentRow label="Payment Type">{fallback.method || 'N/A'}</PaymentRow>
                    <PaymentRow label="Transaction Id">{fallback.transactionId || 'N/A'}</PaymentRow>
                    <PaymentRow label="Amount Paid">{money(fallback.amount)}</PaymentRow>
                </div>
            </div>
        );
    }

    return (
        <ul className="booking-detail-page tw-flex tw-flex-col tw-gap-4">
            {payments.map((p, i) => {
                const dispute = p?.disputes?.length ? p.disputes[p.disputes.length - 1] : null;
                const emiDate = p?.metadata?.emiId ? emiDateFor(p.metadata.emiId) : null;
                return (
                    <li key={p.transaction_id || i} className={frame}>
                        <div className="tw-flex tw-flex-col tw-gap-2 tw-p-4">
                            {p?.metadata?.emiId ? <PaymentRow label="EMI Date">{emiDate}</PaymentRow> : null}
                            <PaymentRow label="Payment Date">{formatDate(p.paymentDate) || 'N/A'}</PaymentRow>
                            <PaymentRow label="Transaction Id">{p.transaction_id || 'N/A'}</PaymentRow>
                            <PaymentRow label="Payment Method">{p.method}</PaymentRow>
                            {p?.originalAmount > 0 ? <PaymentRow label="Original amount">{money(p.originalAmount)}</PaymentRow> : null}
                            <PaymentRow label="Paid amount"><span className="tw-font-semibold tw-text-neutral-900">{money(p.amount)}</span></PaymentRow>

                            {(p?.paymentStatus === 'failed' || p?.refunds?.length > 0 || p?.paymentStatus === 'succeeded' || dispute)
                                ? <div className="tw-mt-1 tw-flex tw-flex-col tw-gap-2 tw-border-t tw-border-neutral-300 tw-pt-3">
                                    {p?.paymentStatus === 'failed'
                                        ? <div className="tw-flex tw-flex-col tw-gap-1">
                                            {p?.failed?.errorDescription ? <i className="tw-text-xs tw-text-red-600">{p.failed.errorDescription}</i> : null}
                                            <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-2">
                                                <Badge color="red">Payment Failed</Badge>
                                                <span className="tw-text-xs tw-text-neutral-500">{formatDate(p?.failed?.paymentDate)}</span>
                                            </div>
                                        </div>
                                        : null}
                                    {p?.refunds?.length > 0
                                        ? <div className="tw-flex tw-flex-col tw-gap-1">
                                            <div><Badge color="blue">Payment Refunded</Badge></div>
                                            {p.refunds.map((rf, j) => (
                                                <div key={j} className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-2 tw-text-xs tw-text-blue-700">
                                                    <span>Refunded Amount : $ {rf?.amount}{rf.status === 'pending' ? <i> (Pending)</i> : null}</span>
                                                    <span>{formatDate(rf?.paymentDate)}</span>
                                                </div>
                                            ))}
                                        </div>
                                        : (p?.paymentStatus === 'succeeded' ? <div><Badge color="green">Payment successful</Badge></div> : null)}
                                    {dispute
                                        ? <DisputeInfo dispute={dispute} formatDate={formatDate} dueLabel={dueLabel} canRespond={canRespondDispute} onDisputeClose={onDisputeClose} />
                                        : null}
                                </div>
                                : null}
                        </div>
                        {/* Shared component (maroon bar, own border): sits inside the grey block, as on the old page. */}
                        <div className="tw-px-4 tw-pb-4">
                            <PaymentDetails transactionId={p.transaction_id} />
                        </div>
                    </li>
                );
            })}
        </ul>
    );
};

/** Payments Notes (`editNotes`): same sign and colour mapping as before (diff → "+" in red). */
export const PaymentNotesCard = ({ notes }) => (
    <Card id="payment-notes" title="Payment Notes" icon={StickyNote}>
        <ul className="tw-flex tw-flex-col tw-gap-1.5">
            {notes.map((note, i) => (
                <li key={i} className={`tw-text-sm ${note.diff ? 'tw-text-red-600' : 'tw-text-green-700'}`}>
                    {note.diff ? '+' : '-'} {note.difference ? <b>{money(Math.abs(note.difference))}</b> : null} {note.notes}
                </li>
            ))}
        </ul>
    </Card>
);
